import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { notificationService } from '../../services/api';
import { formatDistanceToNow } from 'date-fns';
import { FiBell, FiTrash2, FiCheck } from 'react-icons/fi';
import './Notifications.css';

const getNotificationIcon = (type) => {
  switch (type) {
    case 'like': return '👍';
    case 'comment': return '💬';
    case 'tag': return '🏷️';
    case 'reply': return '↩️';
    case 'message': return '✉️';
    case 'post': return '📝';
    case 'event': return '📅';
    case 'birthday': return '🎂';
    default: return '🔔';
  }
};

const getNotificationLink = (referenceType, referenceID) => {
  if (referenceType === 'post') return `/post/${referenceID}`;
  if (referenceType === 'photo') return '/photos';
  if (referenceType === 'comment') return `/post/${referenceID}`;
  return '/';
};

const groupNotifications = (notifications) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);

  const groups = { today: [], thisWeek: [], earlier: [] };

  notifications.forEach(n => {
    const date = new Date(n.createdDate + 'Z');
    if (date >= today) groups.today.push(n);
    else if (date >= weekAgo) groups.thisWeek.push(n);
    else groups.earlier.push(n);
  });

  return groups;
};

const Notifications = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchNotifications = useCallback(async (pageNum = 1, append = false) => {
    try {
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);

      const response = await notificationService.getNotifications(pageNum, 20);
      if (response.success) {
        if (append) {
          setNotifications(prev => [...prev, ...response.data.notifications]);
        } else {
          setNotifications(response.data.notifications);
        }
        setUnreadCount(response.data.unreadCount);
        setHasMore(response.data.hasMore);
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleMarkAsRead = async (notification) => {
    if (!notification.isRead) {
      await notificationService.markAsRead(notification.notificationID);
      setNotifications(prev => prev.map(n =>
        n.notificationID === notification.notificationID ? { ...n, isRead: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    navigate(getNotificationLink(notification.referenceType, notification.referenceID));
  };

  const handleMarkAllAsRead = async () => {
    await notificationService.markAllAsRead();
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    setUnreadCount(0);
  };

  const handleDelete = async (e, notificationId) => {
    e.stopPropagation();
    await notificationService.deleteNotification(notificationId);
    setNotifications(prev => prev.filter(n => n.notificationID !== notificationId));
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchNotifications(nextPage, true);
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const groups = groupNotifications(notifications);

  const renderNotification = (notification) => (
    <div
      key={notification.notificationID}
      className={`notification-item ${!notification.isRead ? 'unread' : ''}`}
      onClick={() => handleMarkAsRead(notification)}
    >
      <div className="notification-avatar">
        {notification.actor?.profilePhotoURL ? (
          <img src={notification.actor.profilePhotoURL} alt="" className="avatar avatar-sm" />
        ) : (
          <div className="avatar avatar-sm">
            {getInitials(notification.actor?.firstName, notification.actor?.lastName)}
          </div>
        )}
        <span className="notification-type-icon">
          {getNotificationIcon(notification.notificationType)}
        </span>
      </div>

      <div className="notification-body">
        <p className="notification-text">
          <strong>{notification.actor?.fullName}</strong> {notification.content}
        </p>
        <span className="notification-time">
          {formatDistanceToNow(new Date(notification.createdDate + 'Z'), { addSuffix: true })}
        </span>
      </div>

      {!notification.isRead && <div className="notification-dot" />}

      <button
        className="notification-delete"
        onClick={(e) => handleDelete(e, notification.notificationID)}
        title="Delete"
      >
        <FiTrash2 />
      </button>
    </div>
  );

  return (
    <div className="notifications-page">
      <div className="container">
        <div className="page-header">
          <h1><FiBell className="page-header-icon" /> Notifications</h1>
          {unreadCount > 0 && (
            <button className="btn btn-secondary btn-sm" onClick={handleMarkAllAsRead}>
              <FiCheck /> Mark All as Read ({unreadCount})
            </button>
          )}
        </div>

        {loading ? (
          <div className="loading-state">
            <span className="spinner spinner-lg"></span>
            <p>Loading notifications...</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="empty-state card">
            <div className="empty-state-icon">🔔</div>
            <h3 className="empty-state-title">No notifications yet</h3>
            <p>When family members interact with your posts you'll see it here.</p>
          </div>
        ) : (
          <div className="notifications-list card">
            {groups.today.length > 0 && (
              <div className="notification-group">
                <h3 className="notification-group-title">Today</h3>
                {groups.today.map(renderNotification)}
              </div>
            )}

            {groups.thisWeek.length > 0 && (
              <div className="notification-group">
                <h3 className="notification-group-title">This Week</h3>
                {groups.thisWeek.map(renderNotification)}
              </div>
            )}

            {groups.earlier.length > 0 && (
              <div className="notification-group">
                <h3 className="notification-group-title">Earlier</h3>
                {groups.earlier.map(renderNotification)}
              </div>
            )}

            {hasMore && (
              <div className="load-more">
                <button
                  className="btn btn-secondary"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    <><span className="spinner spinner-sm"></span> Loading...</>
                  ) : 'Load More'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;